import React from "react";
import {
  MdAdd,
  MdCampaign,
  MdClose,
  MdDelete,
  MdEdit,
  MdImage,
  MdLink,
  MdRefresh,
  MdSave,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import RemoteSelect, { type RemoteSelectOption } from "components/ui/RemoteSelect";
import Select from "components/ui/Select";
import { r2Service } from "services/r2Service";
import {
  homeBannersService,
  type AdminHomeBanner,
  type HomeBannerActionType,
  type HomeBannerCourseOption,
  type HomeBannerPayload,
  type HomeBannerTeacherOption,
} from "services/homeBannersService";
import { resolveStoredUploadPreview } from "lib/storedUploadImage";
import { toastError, toastSuccess } from "lib/toast";

const inputClass =
  "mt-1 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-brand-400 focus:ring-2 focus:ring-brand-500/20";

const emptyForm: HomeBannerPayload = {
  title_ar: "",
  title_en: "",
  subtitle_ar: "",
  subtitle_en: "",
  image: "",
  action_type: "none",
  action_value: "",
  sort_order: 0,
  is_active: true,
};

const IN_APP_TARGET_OPTIONS = [
  { value: "/(tabs)", ar: "الرئيسية", en: "Home" },
  { value: "/(tabs)/my-courses", ar: "كورساتي", en: "My courses" },
  { value: "/wallet", ar: "المحفظة", en: "Wallet" },
  { value: "/downloads", ar: "التحميلات", en: "Downloads" },
  { value: "/notifications", ar: "الإشعارات", en: "Notifications" },
  { value: "/(tabs)/messages", ar: "الرسائل", en: "Messages" },
  { value: "/(tabs)/profile", ar: "الملف الشخصي", en: "Profile" },
  { value: "/(tabs)/settings", ar: "الإعدادات", en: "Settings" },
] as const;

function formFromBanner(banner: AdminHomeBanner): HomeBannerPayload {
  return {
    title_ar: banner.title_ar ?? "",
    title_en: banner.title_en ?? "",
    subtitle_ar: banner.subtitle_ar ?? "",
    subtitle_en: banner.subtitle_en ?? "",
    image: banner.image,
    action_type: banner.action_type,
    action_value: banner.action_value ?? "",
    sort_order: banner.sort_order,
    is_active: banner.is_active,
  };
}

function actionLabel(type: HomeBannerActionType, isArabic: boolean): string {
  if (type === "external_url") return isArabic ? "رابط خارجي" : "External URL";
  if (type === "in_app") return isArabic ? "صفحة داخل التطبيق" : "In-app page";
  if (type === "course") return isArabic ? "كورس معين" : "Specific course";
  if (type === "teacher") return isArabic ? "صفحة معلم معين" : "Specific teacher page";
  return isArabic ? "بدون Target" : "No target";
}

function imagePreview(banner: AdminHomeBanner | null, form: HomeBannerPayload): string {
  if (banner?.image_url) return banner.image_url;
  return resolveStoredUploadPreview(form.image, null);
}

function BannerForm({
  banner,
  isArabic,
  onClose,
  onSaved,
}: {
  banner: AdminHomeBanner | null;
  isArabic: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = React.useState<HomeBannerPayload>(
    banner ? formFromBanner(banner) : { ...emptyForm },
  );
  const [uploading, setUploading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [uploadedPreview, setUploadedPreview] = React.useState(banner?.image_url ?? "");
  const [selectedCourse, setSelectedCourse] = React.useState<HomeBannerCourseOption | null>(null);
  const [selectedTeacher, setSelectedTeacher] = React.useState<HomeBannerTeacherOption | null>(null);
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  const update = <K extends keyof HomeBannerPayload>(key: K, value: HomeBannerPayload[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const updateActionType = (actionType: HomeBannerActionType) => {
    setForm((previous) => ({
      ...previous,
      action_type: actionType,
      action_value: actionType === "in_app" ? IN_APP_TARGET_OPTIONS[0].value : "",
    }));
    setSelectedCourse(null);
    setSelectedTeacher(null);
  };

  const inAppTargetOptions = React.useMemo(() => {
    const currentTarget = form.action_value?.trim();
    if (!currentTarget || IN_APP_TARGET_OPTIONS.some((option) => option.value === currentTarget)) {
      return IN_APP_TARGET_OPTIONS;
    }

    return [
      {
        value: currentTarget,
        ar: `الهدف الحالي (${currentTarget})`,
        en: `Current target (${currentTarget})`,
      },
      ...IN_APP_TARGET_OPTIONS,
    ];
  }, [form.action_value]);

  const fetchCourseOptions = React.useCallback(async (term: string): Promise<RemoteSelectOption[]> => {
    try {
      const options = await homeBannersService.courseOptions(term);
      const selected = options.find((option) => option.id === Number(form.action_value));
      if (selected) setSelectedCourse(selected);

      return options.map((option) => ({
        value: option.id,
        label: option.title,
        description: [option.subject, option.instructor].filter(Boolean).join(" · ") || `#${option.id}`,
      }));
    } catch {
      return [];
    }
  }, [form.action_value]);

  const fetchTeacherOptions = React.useCallback(async (term: string): Promise<RemoteSelectOption[]> => {
    try {
      const options = await homeBannersService.teacherOptions(term);
      const selected = options.find((option) => option.target === form.action_value);
      if (selected) setSelectedTeacher(selected);

      return options.map((option) => ({
        value: option.target,
        label: option.name,
        description: [option.subject, `${option.courses_count} ${isArabic ? "كورس" : "courses"}`]
          .filter(Boolean)
          .join(" · "),
      }));
    } catch {
      return [];
    }
  }, [form.action_value, isArabic]);

  const pickImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError(isArabic ? "اختار صورة فقط." : "Please choose an image file.");
      return;
    }

    setUploading(true);
    setError("");
    try {
      const uploaded = await r2Service.uploadWithPresign(file, "images");
      update("image", uploaded.key);
      setUploadedPreview(uploaded.url);
    } catch (uploadError: any) {
      setError(uploadError?.message || (isArabic ? "فشل رفع الصورة." : "Image upload failed."));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.image?.trim()) {
      setError(isArabic ? "صورة البانر مطلوبة." : "Banner image is required.");
      return;
    }
    if (form.action_type !== "none" && !form.action_value?.trim()) {
      setError(isArabic ? "اكتب الـ Target المطلوب." : "Enter the target value.");
      return;
    }
    if (form.action_type === "external_url" && !/^https?:\/\//i.test(form.action_value?.trim() ?? "")) {
      setError(isArabic ? "الرابط الخارجي يجب أن يبدأ بـ http أو https." : "External URL must start with http or https.");
      return;
    }
    if (form.action_type === "in_app" && !form.action_value?.trim().startsWith("/")) {
      setError(isArabic ? "Target داخل التطبيق يجب أن يبدأ بعلامة /." : "In-app target must start with /.");
      return;
    }
    if (form.action_type === "course" && !/^\d+$/.test(form.action_value?.trim() ?? "")) {
      setError(isArabic ? "اختار كورسًا من القائمة." : "Choose a course from the list.");
      return;
    }
    if (form.action_type === "teacher" && !/^\/subject\/\d+\/teacher\/\d+$/.test(form.action_value?.trim() ?? "")) {
      setError(isArabic ? "اختار مدرسًا من القائمة." : "Choose a teacher from the list.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload: HomeBannerPayload = {
        ...form,
        title_ar: form.title_ar?.trim() || null,
        title_en: form.title_en?.trim() || null,
        subtitle_ar: form.subtitle_ar?.trim() || null,
        subtitle_en: form.subtitle_en?.trim() || null,
        action_value: form.action_type === "none" ? null : form.action_value?.trim() || null,
        sort_order: Number(form.sort_order) || 0,
      };
      if (banner) {
        await homeBannersService.update(banner.id, payload);
      } else {
        await homeBannersService.create(payload);
      }
      toastSuccess(isArabic ? "تم حفظ البانر." : "Banner saved.");
      onSaved();
      onClose();
    } catch (saveError: any) {
      setError(saveError?.response?.data?.message || (isArabic ? "تعذر حفظ البانر." : "Could not save the banner."));
    } finally {
      setSaving(false);
    }
  };

  const preview = uploadedPreview || imagePreview(banner, form);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 p-4" role="presentation">
      <div className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-white/10 bg-surface p-5 shadow-2xl sm:p-7" dir={isArabic ? "rtl" : "ltr"}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-500/15 text-brand-300"><MdCampaign className="h-6 w-6" /></div>
            <h2 className="text-xl font-black text-white">{banner ? (isArabic ? "تعديل البانر" : "Edit banner") : (isArabic ? "إضافة بانر" : "Add banner")}</h2>
            <p className="mt-1 text-sm text-slate-400">{isArabic ? "ارفع صورة وحدد الرابط أو الصفحة التي يفتحها البانر." : "Upload an image and choose the link or app page it should open."}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-slate-300 transition hover:bg-white/10" aria-label={isArabic ? "إغلاق" : "Close"}><MdClose className="h-6 w-6" /></button>
        </div>

        {error ? <div className="mt-5 rounded-xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div> : null}

        <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_1.15fr]">
          <div>
            <p className="text-sm font-bold text-slate-200">{isArabic ? "صورة البانر" : "Banner image"}</p>
            <div className="mt-2 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/40">
              {preview ? <img src={preview} alt="" className="aspect-[16/7] w-full object-cover" /> : <div className="flex aspect-[16/7] items-center justify-center text-slate-500"><MdImage className="h-10 w-10" /></div>}
              <div className="border-t border-white/10 p-3">
                <input ref={fileRef} type="file" accept="image/*" onChange={(event) => void pickImage(event)} className="hidden" />
                <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-brand-600 disabled:opacity-50"><MdImage className="h-5 w-5" />{uploading ? (isArabic ? "جارٍ الرفع…" : "Uploading…") : (isArabic ? "اختيار صورة" : "Choose image")}</button>
                <p className="mt-2 text-center text-xs text-slate-500">{isArabic ? "يفضل مقاس 16:7 أو قريب منه." : "16:7 ratio is recommended."}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-semibold text-slate-200">{isArabic ? "العنوان عربي" : "Arabic title"}<input className={inputClass} value={form.title_ar ?? ""} onChange={(event) => update("title_ar", event.target.value)} maxLength={255} /></label>
              <label className="block text-sm font-semibold text-slate-200">{isArabic ? "العنوان إنجليزي" : "English title"}<input className={inputClass} value={form.title_en ?? ""} onChange={(event) => update("title_en", event.target.value)} maxLength={255} /></label>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-semibold text-slate-200">{isArabic ? "وصف عربي" : "Arabic subtitle"}<textarea className={`${inputClass} resize-y`} rows={2} value={form.subtitle_ar ?? ""} onChange={(event) => update("subtitle_ar", event.target.value)} maxLength={500} /></label>
              <label className="block text-sm font-semibold text-slate-200">{isArabic ? "وصف إنجليزي" : "English subtitle"}<textarea className={`${inputClass} resize-y`} rows={2} value={form.subtitle_en ?? ""} onChange={(event) => update("subtitle_en", event.target.value)} maxLength={500} /></label>
            </div>
            <div className="grid gap-4 sm:grid-cols-[1fr_150px]">
              <Select
                label={isArabic ? "نوع الـ Target" : "Target type"}
                value={form.action_type}
                onChange={(next) => updateActionType((next as HomeBannerActionType) ?? "none")}
                options={(["none", "external_url", "in_app", "course", "teacher"] as HomeBannerActionType[]).map((type) => ({
                  value: type,
                  label: actionLabel(type, isArabic),
                }))}
                isArabic={isArabic}
                allowEmpty={false}
                clearable={false}
                portal
              />
              <label className="block text-sm font-semibold text-slate-200">{isArabic ? "الترتيب" : "Order"}<input type="number" min={0} className={inputClass} value={form.sort_order} onChange={(event) => update("sort_order", Number(event.target.value) || 0)} /></label>
            </div>
            {form.action_type === "external_url" ? <label className="block text-sm font-semibold text-slate-200"><span className="inline-flex items-center gap-1"><MdLink className="h-4 w-4" />{isArabic ? "الرابط الخارجي" : "External URL"}</span><input className={inputClass} value={form.action_value ?? ""} onChange={(event) => update("action_value", event.target.value)} placeholder="https://example.com" maxLength={2048} /></label> : null}
            {form.action_type === "in_app" ? <Select
              label={isArabic ? "الصفحة داخل التطبيق" : "App page"}
              value={form.action_value ?? IN_APP_TARGET_OPTIONS[0].value}
              onChange={(next) => update("action_value", String(next ?? IN_APP_TARGET_OPTIONS[0].value))}
              options={inAppTargetOptions.map((option) => ({ value: option.value, label: isArabic ? option.ar : option.en }))}
              isArabic={isArabic}
              searchable
              allowEmpty={false}
              clearable={false}
              portal
            /> : null}
            {form.action_type === "course" ? <div><label className="mb-1 block text-sm font-semibold text-slate-200">{isArabic ? "اختيار الكورس" : "Choose a course"}</label><RemoteSelect
              value={form.action_value ?? null}
              valueLabel={selectedCourse?.title ?? (form.action_value ? `#${form.action_value}` : undefined)}
              onChange={(next) => {
                update("action_value", next === null ? "" : String(next));
                if (next === null) setSelectedCourse(null);
              }}
              fetchOptions={fetchCourseOptions}
              placeholder={isArabic ? "اختار الكورس" : "Select a course"}
              searchPlaceholder={isArabic ? "ابحث باسم الكورس..." : "Search by course name..."}
              className="mt-1"
            /></div> : null}
            {form.action_type === "teacher" ? <div><label className="mb-1 block text-sm font-semibold text-slate-200">{isArabic ? "اختيار المدرس" : "Choose a teacher"}</label><RemoteSelect
              value={form.action_value ?? null}
              valueLabel={selectedTeacher ? `${selectedTeacher.name}${selectedTeacher.subject ? ` · ${selectedTeacher.subject}` : ""}` : (form.action_value ? form.action_value : undefined)}
              onChange={(next) => {
                update("action_value", next === null ? "" : String(next));
                if (next === null) setSelectedTeacher(null);
              }}
              fetchOptions={fetchTeacherOptions}
              placeholder={isArabic ? "اختار المدرس" : "Select a teacher"}
              searchPlaceholder={isArabic ? "ابحث باسم المدرس أو المادة..." : "Search by teacher or subject..."}
              className="mt-1"
            /></div> : null}
            <label className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm font-semibold text-slate-200"><span className="inline-flex items-center gap-2">{form.is_active ? <MdVisibility className="h-5 w-5 text-emerald-300" /> : <MdVisibilityOff className="h-5 w-5 text-slate-500" />}{isArabic ? "البانر ظاهر في التطبيق" : "Banner visible in app"}</span><input type="checkbox" checked={form.is_active} onChange={(event) => update("is_active", event.target.checked)} className="h-5 w-5 accent-brand-500" /></label>
          </div>
        </div>

        <div className="mt-7 flex justify-end gap-2 border-t border-white/10 pt-5"><button type="button" onClick={onClose} className="rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/15">{isArabic ? "إلغاء" : "Cancel"}</button><button type="button" disabled={saving || uploading} onClick={() => void save()} className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-50"><MdSave className="h-5 w-5" />{saving ? "…" : isArabic ? "حفظ البانر" : "Save banner"}</button></div>
      </div>
    </div>
  );
}

export default function HomeBannersPage() {
  const isArabic = useAppLanguage() === "ar";
  const [items, setItems] = React.useState<AdminHomeBanner[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [editor, setEditor] = React.useState<AdminHomeBanner | null | undefined>(undefined);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setItems(await homeBannersService.list());
    } catch {
      setError(isArabic ? "تعذر تحميل البانرات." : "Could not load banners.");
    } finally {
      setLoading(false);
    }
  }, [isArabic]);

  React.useEffect(() => { void load(); }, [load]);

  const remove = async (banner: AdminHomeBanner) => {
    if (!window.confirm(isArabic ? "هل تريد حذف هذا البانر؟" : "Delete this banner?")) return;
    try {
      await homeBannersService.remove(banner.id);
      toastSuccess(isArabic ? "تم حذف البانر." : "Banner deleted.");
      await load();
    } catch {
      toastError(isArabic ? "تعذر حذف البانر." : "Could not delete the banner.");
    }
  };

  return (
    <section dir={isArabic ? "rtl" : "ltr"} className="space-y-5">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div><div className="mb-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-500/15 text-brand-300"><MdCampaign className="h-6 w-6" /></div><h1 className="text-2xl font-black text-white sm:text-3xl">{isArabic ? "بانرات الصفحة الرئيسية" : "Home banners"}</h1><p className="mt-1 text-sm text-slate-400">{isArabic ? "أضف صورًا ترويجية وحدد الرابط أو الصفحة التي تفتح عند الضغط." : "Add promotional images and control the link or app page opened on press."}</p></div>
        <div className="flex gap-2"><button type="button" onClick={() => void load()} className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10"><MdRefresh className="h-5 w-5" />{isArabic ? "تحديث" : "Refresh"}</button><button type="button" onClick={() => setEditor(null)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-600"><MdAdd className="h-5 w-5" />{isArabic ? "إضافة بانر" : "Add banner"}</button></div>
      </div>

      {loading ? <div className="ef-panel py-16 text-center text-sm text-slate-400">{isArabic ? "جارٍ تحميل البانرات…" : "Loading banners…"}</div> : error ? <div className="ef-panel py-16 text-center text-sm text-red-300">{error}<button type="button" onClick={() => void load()} className="mx-2 font-bold text-brand-300 hover:underline">{isArabic ? "إعادة المحاولة" : "Retry"}</button></div> : items.length === 0 ? <div className="ef-panel py-16 text-center"><MdCampaign className="mx-auto h-12 w-12 text-slate-600" /><h2 className="mt-3 text-lg font-bold text-white">{isArabic ? "لا توجد بانرات بعد" : "No banners yet"}</h2><p className="mt-1 text-sm text-slate-400">{isArabic ? "أضف أول بانر ليظهر في تطبيق الطلاب." : "Add the first banner to show it in the student app."}</p></div> : <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">{items.map((banner) => <article key={banner.id} className="overflow-hidden rounded-3xl border border-white/10 bg-surface shadow-xl"><div className="relative aspect-[16/7] bg-slate-950/50">{banner.image_url ? <img src={banner.image_url} alt={banner.title_ar || banner.title_en || ""} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-slate-600"><MdImage className="h-10 w-10" /></div>}<span className={`absolute start-3 top-3 rounded-full px-2.5 py-1 text-xs font-bold ${banner.is_active ? "bg-emerald-500/90 text-white" : "bg-slate-700/90 text-slate-200"}`}>{banner.is_active ? (isArabic ? "ظاهر" : "Visible") : (isArabic ? "مخفي" : "Hidden")}</span><span className="absolute end-3 top-3 rounded-full bg-slate-950/75 px-2.5 py-1 text-xs font-bold text-white">#{banner.sort_order}</span></div><div className="p-4"><h2 className="truncate text-lg font-bold text-white">{(isArabic ? banner.title_ar : banner.title_en) || banner.title_ar || banner.title_en || (isArabic ? "بدون عنوان" : "Untitled")}</h2><p className="mt-1 line-clamp-2 min-h-10 text-sm text-slate-400">{(isArabic ? banner.subtitle_ar : banner.subtitle_en) || banner.subtitle_ar || banner.subtitle_en || "—"}</p><div className="mt-3 flex items-center gap-2 text-xs text-brand-200"><MdLink className="h-4 w-4" />{actionLabel(banner.action_type, isArabic)}{banner.action_value ? <span className="max-w-[190px] truncate text-slate-500">· {banner.action_value}</span> : null}</div><div className="mt-4 flex gap-2 border-t border-white/10 pt-3"><button type="button" onClick={() => setEditor(banner)} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/15"><MdEdit className="h-4 w-4" />{isArabic ? "تعديل" : "Edit"}</button><button type="button" onClick={() => void remove(banner)} className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-red-500/10 px-3 py-2 text-sm font-semibold text-red-200 hover:bg-red-500/20"><MdDelete className="h-4 w-4" />{isArabic ? "حذف" : "Delete"}</button></div></div></article>)}</div>}

      {editor !== undefined ? <BannerForm banner={editor} isArabic={isArabic} onClose={() => setEditor(undefined)} onSaved={() => void load()} /> : null}
    </section>
  );
}
